import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import './css/RecipesList.css';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from './components/SEO';

function ContentCreatorRecipes() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [contentCreator, setContentCreator] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const observer = useRef();
  const loadingRef = useRef(null);

  const RECIPES_PER_PAGE = 12;

  // Setup Intersection Observer for infinite scrolling
  const lastRecipeElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreRecipes();
      }
    }, { threshold: 0.5 });
    
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore]);

  // Initial recipe fetch
  useEffect(() => {
    if (id) {
      fetchContentCreatorInfo();
      fetchContentCreatorRecipes(1, true);
    }
  }, [id]);

  // Function to fetch content creator information
  const fetchContentCreatorInfo = async () => {
    try {
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/content-creators/${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Fetched content creator info:', data);
      
      setContentCreator(data);
    } catch (error) {
      console.error('Error fetching content creator info:', error);
    }
  };

  // Function to fetch content creator recipes with pagination
  const fetchContentCreatorRecipes = async (page, resetList = false) => {
    try {
      resetList ? setLoading(true) : setLoadingMore(true);
      setError(null);
      
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/content-creators/${id}/recipes?page=${page}&limit=${RECIPES_PER_PAGE}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`Fetched content creator recipes for page ${page}:`, data);
      
      let newRecipes = [];
      
      // Check if data has recipes property
      if (data && data.recipes && Array.isArray(data.recipes)) {
        newRecipes = data.recipes;
      } else {
        console.error('Received invalid data format:', data);
        newRecipes = [];
      }
      
      // Check pagination info
      if (data.pagination) {
        setHasMore(data.pagination.hasMore || page < data.pagination.totalPages);
      } else {
        // Fallback: check if we got fewer recipes than requested
        setHasMore(newRecipes.length === RECIPES_PER_PAGE);
      }
      
      // Update the recipes state
      if (resetList) {
        setRecipes(newRecipes);
      } else {
        setRecipes(prevRecipes => [...prevRecipes, ...newRecipes]);
      }
    } catch (error) {
      console.error('Error fetching content creator recipes:', error);
      setError('Failed to load recipes. Please try again.');
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Function to load more recipes when scrolling
  const loadMoreRecipes = () => {
    if (!hasMore || loading || loadingMore) return;
    
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchContentCreatorRecipes(nextPage);
  };

  // Handle back button click
  const handleGoBack = () => {
    navigate(-1);
  };

  // Create a skeleton card component for reuse
  const SkeletonRecipeCard = () => (
    <div className="recipe-card skeleton-card">
      <div className="skeleton-image"></div>
      <div className="skeleton-content">
        <div className="skeleton-title"></div>
        <div className="skeleton-stats">
          <div className="skeleton-stat"></div>
          <div className="skeleton-stat"></div>
        </div>
      </div>
    </div>
  );

  // Render skeleton grid for all recipes
  const renderSkeletonGrid = () => (
    <div className="recipes-grid">
      {[...Array(12)].map((_, index) => (
        <SkeletonRecipeCard key={`skeleton-grid-${index}`} />
      ))}
    </div>
  );

  // Generate dynamic SEO data for content creator
  const generateCreatorSEOData = () => {
    if (contentCreator) {
      return {
        title: `${contentCreator.name} - Recipes by ${contentCreator.name} | Savory`,
        description: `Discover delicious recipes created by ${contentCreator.name}. Explore their collection of ${recipes.length > 0 ? recipes.length : ''} recipes with detailed nutritional information and cooking instructions.`,
        keywords: `${contentCreator.name}, recipes, cooking, chef, content creator, YouTube recipes`,
        url: `/content-creator/${id}`,
        image: contentCreator.profile_picture_url || '/SavLogo.png',
        type: 'profile',
        breadcrumb: [
          { name: "Home", url: "/" },
          { name: "Content Creators", url: "/" },
          { name: contentCreator.name, url: `/content-creator/${id}` }
        ]
      };
    }
    
    return {
      title: "Content Creator - Savory",
      description: "Explore recipes from talented content creators on Savory.",
      keywords: "content creator, recipes, cooking, chef",
      url: `/content-creator/${id}`,
      breadcrumb: [
        { name: "Home", url: "/" },
        { name: "Content Creators", url: "/" }
      ]
    };
  };

  const seoData = generateCreatorSEOData();

  return (
    <div className="recipes-list-container">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={seoData.url}
        image={seoData.image}
        type={seoData.type}
        breadcrumb={seoData.breadcrumb}
        author={contentCreator?.name}
      />
      {/* Back button */}
      <div className="back-button-container">
        <Link to="#" onClick={handleGoBack} className="back-button-details">
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className="backButtonImg"
          >
            <path 
              d="M19 12H5M12 19L5 12L12 5" 
              stroke="#666" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </Link>
      </div>

      {/* Content Creator Header */}
      <motion.div 
        className="content-creator-header"
        style={{
          backgroundImage: contentCreator?.banner_picture_url 
            ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${contentCreator.banner_picture_url})`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="content-creator-info">
          {contentCreator ? (
            <>
              {contentCreator.profile_picture_url && (
                <motion.div 
                  className="content-creator-avatar"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <img 
                    src={contentCreator.profile_picture_url} 
                    alt={contentCreator.name}
                    className="content-creator-profile-pic"
                  />
                </motion.div>
              )}
              <h1 className="content-creator-name">{contentCreator.name}</h1>
              {contentCreator.description && (
                <p className="content-creator-description">{contentCreator.description}</p>
              )}
              <p className="content-creator-subtitle">Recipes by {contentCreator.name}</p>
            </>
          ) : (
            <>
              <div className="content-creator-avatar">
                <div className="content-creator-profile-pic skeleton-creator-profile"></div>
              </div>
              <div className="content-creator-name skeleton-creator-title"></div>
              <div className="content-creator-description skeleton-creator-description"></div>
              <div className="content-creator-subtitle skeleton-creator-subtitle"></div>
            </>
          )}
        </div>
      </motion.div>

      <div className="TrendingFilter">
        <div className="inner-container">
          <div className="header2">
            <h1 className="titleRecipes">
              {contentCreator ? `${contentCreator.name}'s Recipes` : 'Recipes'}
            </h1>
            {recipes.length > 0 && (
              <span className="recipe-count">{recipes.length} recipe{recipes.length !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => fetchContentCreatorRecipes(1, true)} className="retry-button">
            Try Again
          </button>
        </div>
      )}

      {loading ? (
        renderSkeletonGrid()
      ) : (
        <>
          {recipes.length === 0 ? (
            <div className="no-recipes-found">
              <h2>No recipes found</h2>
              <p>This content creator hasn't shared any recipes yet.</p>
            </div>
          ) : (
            <motion.div 
              className="recipes-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              {recipes.map((recipe, index) => (
                <motion.div 
                  key={recipe.id} 
                  className="recipe-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.2,
                    delay: Math.min(index * 0.05, 0.5),
                    ease: "easeOut"
                  }}
                  whileHover={{ 
                    y: -5,
                    scale: 1.03,
                    boxShadow: "0 12px 20px rgba(0,0,0,0.08)"
                  }}
                  whileTap={{ scale: 0.98 }}
                  // Attach the ref to the last element for infinite scrolling detection
                  ref={index === recipes.length - 1 ? lastRecipeElementRef : null}
                >
                  <Link to={`/recipe-details/${recipe.id}`}>
                    <motion.div 
                      className="recipe-image-wrapper"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                      <motion.img 
                        src={recipe.url || 'https://via.placeholder.com/300x200?text=No+Image'} 
                        alt={recipe.name} 
                        className="recipe-image"
                        initial={{ scale: 1.0 }}
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error';
                        }}
                      />
                    </motion.div>
                    <motion.div 
                      className="recipe-content"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.15, delay: 0.1 }}
                    >
                      <motion.h2
                        whileHover={{ color: "#FF6B6B" }}
                        transition={{ duration: 0.15 }}
                      >
                        {recipe.name}
                      </motion.h2>
                      
                      <motion.div
                        className="recipe-stats"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15, delay: 0.15 }}
                      >
                        <p className='calories'>
                          <strong>Calories:</strong> {recipe.calories || 'N/A'}
                        </p>
                        <p className='proteins'>
                          <strong>Proteins:</strong> {recipe.proteins || 'N/A'} g
                        </p>
                      </motion.div>
                    </motion.div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </>
      )}

      {!loading && recipes.length > 0 && (
        <div className="loading-more" ref={loadingRef}>
          {loadingMore && (
            <div className="loading-spinner-small"></div>
          )}
          {!hasMore && recipes.length > RECIPES_PER_PAGE && (
            <p className="no-more-recipes">No more recipes to load</p>
          )}
        </div>
      )}
    </div>
  );
}

export default ContentCreatorRecipes; 